/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Grid, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { formatDateForInput } from 'utilities/common';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { getEffectiveStatus } from '../recurringPayableConstants';
import { createPaymentSchedule } from '../store/recurringPayables.action';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { GENERATE_RP_PAYMENT_SCHEDULE_FAILURE } from '../store/recurringPayables.types';
import { showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
const useStyles = makeStyles(() => ({
  card: { width: '100%', flexGrow: 1, flexDirection: 'row', justifyContent: 'space-between' },
  cardContainer: { maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' },
  iconRow: { display: 'flex', flexDirection: 'row' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' },
  mb2: { marginBottom: '4px', marginRight: '16px' }
}));

const theme = createTheme({ palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING } });

const toLocalDate = value => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  if (typeof value === 'string') {
    const s = value.includes('T') ? value.split('T')[0] : value;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
};

const makeProjectedPayment = dueDate => ({
  voucherId: 0,
  dueDate,
  statusText: 'Projected'
});

const resolveVoucherStatusTitle = (statusId, enumerations) => {
  if (!statusId || !enumerations) return '';
  const list = enumerations.voucherStatuses || enumerations.voucherStatus || enumerations.enumVoucherStatuses || [];
  const found = list.find(x => String(x.id).toLowerCase() === String(statusId).toLowerCase());
  return found?.title || '';
};

const RecurringPayableScheduleCard = props => {
  const { rpInfo, updateRpInfo, enumerations, isReadOnly = false, history, voucherDetails, createPaymentSchedule } = props;
  const classes = useStyles();
  const rpi = rpInfo?.recurringPayableInformation ?? {};
  const paidOut = Array.isArray(rpInfo?.recurringPayableVouchersPaidOut) ? rpInfo.recurringPayableVouchersPaidOut : [];
  const existing = Array.isArray(voucherDetails) ? voucherDetails : Array.isArray(rpInfo?.voucherDetails) ? rpInfo.voucherDetails : [];

  const [projected, setProjected] = useState([]);
  const [calculatedEnd, setCalculatedEnd] = useState('');
  const [showTooLowWarning, setShowTooLowWarning] = useState(false);

  const effectiveStatus = getEffectiveStatus(rpi);

  const frequencies = useMemo(() => (enumerations?.recurringPayableFrequencies || []).filter(f => f.isActive), [enumerations]);

  const paidOutSorted = useMemo(() => {
    const list = Array.isArray(paidOut) ? paidOut : [];
    return [...list].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [paidOut]);

  const existingSorted = useMemo(() => {
    const byKey = x => `${Number(x.voucherId) || 0}_${toLocalDate(x.dueDate)?.getTime() || 0}`;
    const paidKeys = new Set(paidOutSorted.map(byKey));
    const onlyExisting = (Array.isArray(existing) ? existing : []).filter(v => !paidKeys.has(byKey(v)));
    return onlyExisting.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [existing, paidOutSorted]);

  const minAllowedPayments = useMemo(() => (effectiveStatus === 'ACTIVE' ? paidOutSorted.length : 0), [
    effectiveStatus,
    paidOutSorted.length
  ]);

  const disableNoP = effectiveStatus === 'CANCELLED' || effectiveStatus === 'DENIED' || effectiveStatus === 'EXPIRED';

  const recalc = async () => {
    let start = toLocalDate(rpi.startDate);
    const rawCount = rpi.numberOfPayments;
    const count = Number.isFinite(Number(rawCount)) ? Number(rawCount) : 0;

    if (!rpi.frequencyId || (!start && paidOutSorted.length === 0 && existingSorted.length === 0) || count <= 0) {
      setProjected([]);
      setCalculatedEnd('');
      return;
    }

    let day = start?.getDate();
    if (isIdEqual(rpi.frequencyId, '2a2a792a-9a0e-4a8b-8a35-9a3a0a8cc29f') && day !== 1 && day !== 15) {
      setProjected([]);
      setCalculatedEnd('');
      props.showToast('For semi-monthly frequency, the start date must be either the 1st or the 15th of the month.', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    setShowTooLowWarning(effectiveStatus === 'ACTIVE' && count < minAllowedPayments);

    const combined = [...paidOutSorted, ...existingSorted].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

    const paymentRequest = { frequencyId: rpi.frequencyId, startDate: start?.toISOString() ?? null, numberOfPayments: count };
    const scheduleResp = await createPaymentSchedule(rpInfo.recurringPayableId, paymentRequest);

    if (scheduleResp.type === GENERATE_RP_PAYMENT_SCHEDULE_FAILURE) {
      setProjected([]);
      setCalculatedEnd('');
      props.showToast('Failed to generate payment schedule.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    const scheduleData = scheduleResp.response;
    const remainingDates = scheduleData.length > combined.length ? scheduleData.slice(combined.length) : [];

    const out = [];
    for (let i = 0; i < remainingDates.length; i++) {
      out.push(makeProjectedPayment(new Date(remainingDates[i].paymentDate)));
    }

    const all = [...combined, ...out];
    if (all.length > 0) {
      const last = all[all.length - 1];
      const lastDate = new Date(last.dueDate);
      if (lastDate) {
        const value = formatDateForInput(lastDate.toISOString());
        setCalculatedEnd(value);
        updateRpInfo({
          ...rpInfo,
          recurringPayableInformation: {
            ...(rpInfo?.recurringPayableInformation ?? {}),
            endDate: lastDate.toISOString()
          }
        });
      }
    } else {
      setCalculatedEnd('');
    }

    setProjected(out);
  };

  useEffect(() => {
    recalc();
  }, [rpi.frequencyId, rpi.startDate, rpi.numberOfPayments, paidOutSorted.length, existingSorted.length, effectiveStatus]);

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ mt: '10px', mr: '10px' }}>
        <Box>
          <CardContent sx={{ p: '10px', ml: '10px', pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', p: '20px', flexWrap: 'wrap' }}>
                <Box sx={{ mr: '20px' }}>
                  <Box className={classes.mb2}>
                    <Typography variant="body2">Frequency</Typography>
                  </Box>
                  <Select
                    value={rpi.frequencyId || ZERO_GUID}
                    sx={{ maxWidth: 350, minWidth: 180 }}
                    disabled={isReadOnly}
                    onChange={e => {
                      updateRpInfo({
                        ...rpInfo,
                        recurringPayableInformation: {
                          ...(rpInfo?.recurringPayableInformation ?? {}),
                          frequencyId: e.target.value
                        }
                      });
                    }}
                  >
                    {frequencies.map(opt => (
                      <MenuItem key={String(opt.id)} value={opt.id}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ mr: '20px' }}>
                  <Box className={classes.mb2}>
                    <Typography variant="body2">Start Date</Typography>
                  </Box>
                  <TextField
                    type="date"
                    value={rpi.startDate ? formatDateForInput(rpi.startDate) : ''}
                    sx={{ maxWidth: 360 }}
                    disabled={isReadOnly}
                    InputLabelProps={{ shrink: true }}
                    onChange={e => {
                      let startDate = e.target.value;
                      const patch = {
                        startDate: startDate,
                        entryDate: startDate,
                        invoiceDate: startDate,
                        dueDate: startDate,
                        payrollDate: startDate
                      };
                      updateRpInfo({
                        ...rpInfo,
                        recurringPayableInformation: { ...(rpInfo?.recurringPayableInformation ?? {}), ...patch }
                      });
                    }}
                  />
                </Box>
                <Box sx={{ mr: '20px' }}>
                  <Box className={classes.mb2}>
                    <Typography variant="body2">Number of Payments</Typography>
                  </Box>
                  <TextField
                    type="number"
                    value={rpi.numberOfPayments ?? ''}
                    sx={{ maxWidth: 170 }}
                    disabled={disableNoP}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minAllowedPayments }}
                    onChange={e =>
                      updateRpInfo({
                        ...rpInfo,
                        recurringPayableInformation: {
                          ...(rpInfo?.recurringPayableInformation ?? {}),
                          numberOfPayments: e.target.value === '' ? '' : Number(e.target.value)
                        }
                      })
                    }
                  />
                  {showTooLowWarning && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      Number of payments cannot be less than paid-out vouchers ({paidOutSorted.length}).
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Box className={classes.mb2}>
                    <Typography variant="body2">End Date</Typography>
                  </Box>
                  <TextField type="date" value={calculatedEnd || ''} sx={{ maxWidth: 360 }} disabled InputLabelProps={{ shrink: true }} />
                </Box>
              </Box>
              <Box sx={{ pt: '20px' }}>
                <Paper style={{ minWidth: '600px', maxWidth: '600px', maxHeight: '140px', overflowY: 'auto' }}>
                  <Grid container>
                    {paidOutSorted.map((p, idx) => {
                      const date = toLocalDate(p.dueDate);
                      const label = date ? date.toLocaleDateString() : '—';
                      const canClick = p.voucherId && Number(p.voucherId) > 0;
                      const statusText = resolveVoucherStatusTitle(p.voucherStatusId, enumerations) || 'Paid Out';
                      return (
                        <Grid item md={6} key={`po-${idx}`}>
                          <Box
                            sx={{
                              p: '10px',
                              minWidth: '35%',
                              color: '#41B7C0',
                              cursor: canClick ? 'pointer' : 'default',
                              '&:hover': { textDecoration: canClick ? 'underline' : 'none' }
                            }}
                            onClick={() => canClick && history?.push?.(`/vouchers/${p.voucherId}`)}
                          >
                            {`Payment ${label} (${statusText})`}
                          </Box>
                        </Grid>
                      );
                    })}
                    {existingSorted.map((v, idx) => {
                      const date = toLocalDate(v.dueDate);
                      const label = date ? date.toLocaleDateString() : '—';
                      const canClick = v.voucherId && Number(v.voucherId) > 0;
                      const statusText = v.statusText || v.status || resolveVoucherStatusTitle(v.voucherStatusId, enumerations) || '';
                      return (
                        <Grid item md={6} key={`ex-${idx}`}>
                          <Box
                            sx={{
                              p: '10px',
                              minWidth: '35%',
                              cursor: canClick ? 'pointer' : 'default',
                              '&:hover': { textDecoration: canClick ? 'underline' : 'none' }
                            }}
                            onClick={() => canClick && history?.push?.(`/vouchers/${v.voucherId}`)}
                          >
                            {`Payment ${label}${statusText ? ` (${statusText})` : ''}`}
                          </Box>
                        </Grid>
                      );
                    })}
                    {projected.map((p, idx) => {
                      const date = new Date(p.dueDate);
                      const isoString = date ? date.toISOString() : null;
                      const label = isoString ? formatDateForInput(isoString) : '—';
                      return (
                        <Grid item md={6} key={`pr-${idx}`}>
                          <Box sx={{ p: '10px', minWidth: '35%' }}>{`Payment ${label} (Projected)`}</Box>
                        </Grid>
                      );
                    })}

                    {paidOutSorted.length === 0 && existingSorted.length === 0 && projected.length === 0 && (
                      <Grid item md={12}>
                        <Box sx={{ p: '10px', color: '#777' }}>No scheduled payments to display.</Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Card>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {};

export default compose(
  connect(mapStateToProps, {
    createPaymentSchedule,
    showToast
  })
)(RecurringPayableScheduleCard);
